import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  RealtimeEventTypes,
  RealtimeTopicPrefixes,
  RealtimeTopics,
} from '@interfaces/realtime/realtime-envelope.interface';
import { PartnerDealChanges } from '@interfaces/realtime/events/partner-deal.events';
import { Store } from '@ngrx/store';
import { RealtimeListRefreshService } from '@services/realtime/realtime-list-refresh.service';
import { DEAL_PRESENCE_TEXTS } from '@services/realtime/realtime-presence-text';
import { selectTenantId } from '@state/auth/auth.selectors';
import { AppState } from '@state/index';
import { Observable, map } from 'rxjs';

/**
 * Configuração do `app-realtime-presence-banner` na tela do deal de parceiro (card 3496).
 * Tópico `partner-deal:{id}`, com presença, para o parceiro e para o time interno.
 */
export const PARTNER_DEAL_REALTIME = {
  texts: DEAL_PRESENCE_TEXTS,
  changeEventTypes: [RealtimeEventTypes.PartnerDealChanged] as readonly string[],
  /**
   * O Briefing (card 3878) tem aba própria, que relê sozinha, e publica uma mudança a cada fase do agente: não
   * acende o aviso "Este deal foi atualizado" nem relê o deal inteiro.
   */
  ignoredChanges: [PartnerDealChanges.BriefingChanged] as readonly string[],
  topic: (dealId: string | null | undefined): string | null =>
    dealId ? RealtimeTopics.partnerDeal(dealId) : null,
};

/**
 * Tempo real da lista/kanban de deals de parceiro ("Meus Deals" / "Deals", card 3496). Provido
 * pela tela junto com o `RealtimeListRefreshService`.
 *
 * - Parceiro: não entra em tópico nenhum; o servidor já põe a conexão no grupo automático
 *   `partner:{partnerId}`, que só recebe mudança visível ao parceiro.
 * - Time interno: entra em `partner-deals:{tenantId}` (`setInternalView(true)`).
 * - `refresh$`: `partner-deal.changed` de um desses tópicos (debounce de 300 ms) ou reconexão;
 *   espera o fim do arrasto (`setDragging`). A tela relê pelo REST, sem skeleton.
 */
@Injectable()
export class PartnerDealsRealtimeService {
  private readonly listRefresh = inject(RealtimeListRefreshService);
  private readonly store = inject<Store<AppState>>(Store);

  private readonly tenantId = toSignal(
    this.store.select(selectTenantId).pipe(map(id => (id ? String(id) : null))),
    { initialValue: null }
  );

  readonly refresh$: Observable<void> = this.listRefresh.refresh$({
    eventType: RealtimeEventTypes.PartnerDealChanged,
    topicMatches: topic =>
      topic.startsWith(RealtimeTopicPrefixes.partner) ||
      topic.startsWith(RealtimeTopicPrefixes.partnerDeals),
    // O Briefing (card 3878) publica uma mudança a cada fase do agente e não muda nada que o board mostra.
    shouldRefresh: envelope =>
      (envelope.payload as { change?: unknown } | null)?.change !==
      PartnerDealChanges.BriefingChanged,
  });

  setInternalView(internal: boolean): void {
    const tenantId = this.tenantId();
    this.listRefresh.setTopic(internal && tenantId ? RealtimeTopics.partnerDeals(tenantId) : null);
  }

  setDragging(dragging: boolean): void {
    this.listRefresh.setBusy(dragging);
  }
}
